<script lang="ts">
	import logo from "../../../core/assets/logo.svg";
	import type { Product } from "../../../core/schema/Product.js";
	import type { UploadedFile } from "../../../core/schema/UploadedFile.js";
	import type { Image } from "../../../core/schema/Image.js";
	import DynamicPicture from "./Picture/DynamicPicture.svelte";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/names/appName.js";
	import translations from "./ProductGallery.translations.json";
	import GalleryThumbnails from "./GalleryThumbnails.svelte";
	import DynamicVideo from "./video/DynamicVideo.svelte";
	import { isFileVideo } from "../../../core/utils/isFileVideo.js";
	import { getTranslate } from "../utils/getTranslate.js";

	export let product: Product;
	export let media: Image[];
	export let containerClass = "";
	export { containerClass as class };

	const bigMediaWidth = 600;
	const smallMediaWidth = 300;

	$: videos = media.filter((image) => isFileVideo(image));
	$: images = media.filter((image) => !isFileVideo(image));

	$: [primaryImage, ...otherImages] = images;

	let displayedMedia: UploadedFile | undefined;

	function displayImage(media: UploadedFile): void {
		displayedMedia = media;
	}

	function displayPrimaryImage(): void {
		displayedMedia = primaryImage;
	}

	const translate = getTranslate(translations);
</script>

<div class="relative w-full {containerClass}">
	{#if displayedMedia && isFileVideo(displayedMedia)}
		<DynamicVideo
			class="aspect-[4/3] w-full bg-white"
			videoClass="size-full object-cover"
			video={displayedMedia}
			lightboxId="{product.name} - desktop"
			alt="{translate('photo')} – {product.name} (1/{media.length})"
			width={bigMediaWidth}
			height={Math.round((bigMediaWidth * 3) / 4)}
		/>
	{:else if primaryImage}
		<DynamicPicture
			class="border-lttertiary aspect-[4/3] w-full border bg-white"
			imgClass="size-full object-cover"
			image={displayedMedia ?? primaryImage}
			lightboxId="{product.name} - desktop"
			lightboxWidth={1400}
			width={bigMediaWidth}
			height={Math.round((bigMediaWidth * 3) / 4)}
			bucket="storage"
			loading="eager"
			fetchpriority="high"
			preload
			alt="{translate('photo')} – {product.name} (1/{media.length})"
			sourcesets={{
				">lg": [800, 600],
				">xl": [1200, 900],
			}}
		/>
	{:else}
		<div class="bg-lttertiary-100 flex aspect-[4/3] w-full items-center justify-center">
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-28 opacity-50 lg:w-36 xl:w-40"
				loading="eager"
				width={160}
				height={160}
			/>
		</div>
	{/if}
	{#if videos.length + otherImages.length > 0}
		<GalleryThumbnails ariaPrevious={translate("previous")} ariaNext={translate("next")}>
			{#each videos as video, i}
				<DynamicVideo
					on:mouseover={() => {
						displayImage(video);
					}}
					on:mouseout={displayPrimaryImage}
					class="border-lttertiary aspect-[4/3] w-1/4 shrink-0 cursor-pointer snap-start border bg-white [&:not(:first-child)]:-ml-px"
					videoClass="size-full object-cover"
					autoplay={false}
					{video}
					width={smallMediaWidth}
					height={Math.round((smallMediaWidth * 3) / 4)}
					icon={true}
					lightboxId="{product.name} - desktop"
					alt="{translate('photo')} – {product.name} ({i + 2}/{media.length})"
				/>
			{/each}
			{#each otherImages as image, i}
				<DynamicPicture
					class="border-lttertiary aspect-[4/3] w-1/4 shrink-0 cursor-pointer snap-start border bg-white [&:not(:first-child)]:-ml-px"
					imgClass="size-full object-cover"
					{image}
					lightboxId="{product.name} - desktop"
					lightboxWidth={1000}
					width={smallMediaWidth}
					height={Math.round((smallMediaWidth * 3) / 4)}
					bucket="storage"
					loading="lazy"
					alt="{translate('photo')} – {product.name} ({i + videos.length + 2}/{media.length})"
					on:mouseover={() => {
						displayImage(image);
					}}
					on:mouseout={displayPrimaryImage}
				/>
			{/each}
		</GalleryThumbnails>
	{/if}
</div>
